import React, { createContext, useContext, useEffect, useState } from "react";
import api from "../api";

/**
 * Per-instance brand identity, fetched at runtime.
 *
 * This exists because CRA inlines `REACT_APP_*` into the bundle at build time
 * -- it is not read at runtime -- so the existing REACT_APP_GOOGLE_CLIENT_ID
 * build arg is a precedent that does NOT generalise: using it for branding
 * would mean one image per customer, which is a fork per customer. Branding
 * therefore arrives over HTTP from `GET /api/branding`, which is unauthenticated
 * precisely because the login screen is the first thing that needs it.
 *
 * The colours are written onto `document.documentElement` as **rgb channels**,
 * not hex. Tailwind cannot fold an alpha modifier into a `var()` colour that
 * holds a hex string -- `bg-primary/20` would compile to nothing at all and the
 * class would be silently dead, which is exactly the trap index.css documents
 * for the five theme tokens. Channels plus `<alpha-value>` in tailwind.config.js
 * keep all ~32 alpha-modified brand classes working.
 */

// Mirrors backend/branding.py's DEFAULTS. Not the primary source of defaults --
// the API always returns a complete record -- but the app must still render if
// that call fails, or a backend outage would turn the login page into a white
// screen instead of a page that shows a real error.
export const FALLBACK_BRANDING = {
  org_name: "CRM",
  wordmark_text: "CRM",
  wordmark_accent: "",
  tagline: "",
  org_description: "a business",
  logo_url: "/logo.png",
  logo_icon_url: "/logo_icon.png",
  favicon_url: "/favicon.ico",
  apple_touch_icon_url: "/apple-touch-icon.png",
  login_image_url: "",
  primary: "#4338CA",
  primary_hover: "#3730A3",
  glow: "#00E5FF",
  theme_color: "#060B19",
  document_title: "CRM",
  meta_description: "Mobile-first CRM",
  internal_domains: [],
  google_hd: "",
  currencies: ["EUR", "HUF", "USD", "GBP", "CHF"],
  default_currency: "EUR",
};

const BrandingContext = createContext({ branding: FALLBACK_BRANDING, ready: false });

/** "#4338CA" -> "67 56 202". Returns null for anything that isn't #RRGGBB, so a
 *  malformed value leaves the stylesheet's own default in place rather than
 *  writing an invalid custom property. */
export function hexToChannels(hex) {
  if (typeof hex !== "string") return null;
  const m = /^#([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  const int = parseInt(m[1], 16);
  return `${(int >> 16) & 255} ${(int >> 8) & 255} ${int & 255}`;
}

function applyThemeColors(branding) {
  const root = document.documentElement;
  const mapping = {
    "--brand-primary": branding.primary,
    "--brand-primary-hover": branding.primary_hover,
    "--brand-glow": branding.glow,
  };
  for (const [cssVar, hex] of Object.entries(mapping)) {
    const channels = hexToChannels(hex);
    if (channels) root.style.setProperty(cssVar, channels);
  }
}

function setMeta(name, content) {
  if (!content) return;
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setIconLink(rel, href) {
  if (!href) return;
  // Replace the whole set rather than editing in place: index.html ships three
  // `rel="icon"` links (an .ico plus 16/32px PNGs) for the pre-JS phase, and
  // leaving the sized PNGs pointing at the shipped placeholder would mean a
  // customer's own favicon never wins -- browsers prefer the sized ones.
  // `rel~="icon"` matches whitespace-separated tokens, so it does not also
  // match `apple-touch-icon`.
  document.querySelectorAll(`link[rel~="${rel}"]`).forEach((el) => el.remove());
  const link = document.createElement("link");
  link.setAttribute("rel", rel);
  link.setAttribute("href", href);
  document.head.appendChild(link);
}

function applyDocumentHead(branding) {
  if (branding.document_title) document.title = branding.document_title;
  setMeta("description", branding.meta_description);
  setMeta("theme-color", branding.theme_color);
  setIconLink("icon", branding.favicon_url);
  setIconLink("apple-touch-icon", branding.apple_touch_icon_url);
}

export function BrandingProvider({ children }) {
  const [branding, setBranding] = useState(FALLBACK_BRANDING);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/branding")
      .then((res) => {
        if (cancelled) return;
        // Merge over the fallback so a field the server hasn't learned about
        // yet (an older backend behind a newer bundle) is still present.
        setBranding({ ...FALLBACK_BRANDING, ...res.data });
      })
      .catch((err) => {
        // Not fatal, by design: render the generic product rather than nothing.
        console.error("Branding fetch failed; using built-in defaults:", err);
      })
      .finally(() => {
        if (!cancelled) setReady(true);
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    applyThemeColors(branding);
    applyDocumentHead(branding);
  }, [branding]);

  // Hold first paint until branding resolves, so a customer never sees the
  // product's default indigo repaint into their own colour. This runs in
  // parallel with AuthProvider's /auth/me check, so it costs max(a, b), not
  // a + b. A plain themed ground rather than a spinner: a spinner that appears
  // and vanishes inside ~50ms reads as a flicker.
  if (!ready) return <div className="min-h-screen bg-bg" />;

  return (
    <BrandingContext.Provider value={{ branding, ready, setBranding }}>
      {children}
    </BrandingContext.Provider>
  );
}

export function useBranding() {
  return useContext(BrandingContext);
}
